import BlockRounded from '@mui/icons-material/BlockRounded';
import CheckCircleOutlineRounded from '@mui/icons-material/CheckCircleOutlineRounded';
import CloseRounded from '@mui/icons-material/CloseRounded';
import ComputerRounded from '@mui/icons-material/ComputerRounded';
import ErrorOutlineRounded from '@mui/icons-material/ErrorOutlineRounded';
import MergeRounded from '@mui/icons-material/MergeRounded';
import RestoreRounded from '@mui/icons-material/RestoreRounded';
import ScheduleRounded from '@mui/icons-material/ScheduleRounded';
import SyncProblemRounded from '@mui/icons-material/SyncProblemRounded';
import {
  Alert,
  Box,
  Button,
  Card,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Drawer,
  IconButton,
  MenuItem,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { api, unwrap } from '../../../api/client';
import { useAuth } from '../../../auth/AuthProvider';
import { DataTable } from '../../../components/DataTable';
import { EmptyState, PageHeader } from '../../../components/states';
import { StatusChip } from '../../../components/StatusChip';
import { dateTime, relativeTime } from '../../../lib/format';
import { useNotify } from '../../../lib/notify';
import { useShop } from '../../../shop/ShopProvider';
import { PageTabs } from '../components/UrlTabs';
import {
  Field,
  IconBadge,
  JsonBlock,
  NoAccess,
  QueryError,
  StatGrid,
  StatTile,
} from '../components/ui';
import { useReportParams } from '../components/useReportParams';
import { useUrlTab } from '../components/useUrlTab';
import { entityLabel } from '../lib/entityLinks';
import {
  commandLabel,
  conflictDiff,
  deviceHealth,
  mergeStart,
  parseMerged,
  RESOLUTION_LABEL,
  type Device,
  type SyncConflict,
} from '../lib/sync';

type TabKey = 'devices' | 'conflicts' | 'errors';

export function SyncPage() {
  const { can } = useAuth();
  const canDevices = can('devices.manage');
  const canConflicts = can('sync.view') || can('sync.resolve_conflicts');
  const canErrors = can('sync.view') || canDevices;
  const tabs = [
    { value: 'devices' as const, label: 'Devices', show: canDevices },
    { value: 'conflicts' as const, label: 'Conflicts', show: canConflicts },
    { value: 'errors' as const, label: 'Errors', show: canErrors },
  ];
  const [tab, setTab] = useUrlTab<TabKey>(tabs);
  const any = canDevices || canConflicts || canErrors;
  return (
    <>
      <PageHeader
        title="Devices & sync"
        subtitle="Desktop tills that work offline, the conflicts they raise and commands that failed."
      />
      {!any ? (
        <NoAccess what="devices and sync" />
      ) : (
        <>
          {canDevices && <Overview />}
          <PageTabs value={tab} onChange={setTab} tabs={tabs} />
          {tab === 'devices' && <DevicesTab />}
          {tab === 'conflicts' && <ConflictsTab />}
          {tab === 'errors' && <ErrorsTab />}
        </>
      )}
    </>
  );
}

function useDiagnostics(enabled = true) {
  return useQuery({
    queryKey: ['sync', 'diagnostics'],
    queryFn: () => unwrap(api.GET('/sync/diagnostics')),
    enabled,
  });
}

function Overview() {
  const q = useDiagnostics();
  const d = q.data;
  const active = d?.devices.filter((x) => !x.revoked).length;
  return (
    <>
      {q.error && <QueryError error={q.error} onRetry={() => void q.refetch()} />}
      <StatGrid>
        <StatTile
          label="Active devices"
          value={active}
          icon={<ComputerRounded />}
          loading={q.isLoading}
        />
        <StatTile
          label="Open conflicts"
          value={d?.openConflicts}
          hint={d?.openConflicts ? 'Waiting for review' : 'Nothing to review'}
          icon={<MergeRounded />}
          tone={d?.openConflicts ? 'warning' : 'success'}
          loading={q.isLoading}
        />
        <StatTile
          label="Errors (24 h)"
          value={d?.errorsLast24h}
          icon={<SyncProblemRounded />}
          tone={d?.errorsLast24h ? 'error' : 'success'}
          loading={q.isLoading}
        />
      </StatGrid>
    </>
  );
}

function DevicesTab() {
  const { format } = useShop();
  const qc = useQueryClient();
  const notify = useNotify();
  const q = useDiagnostics();
  const [revoking, setRevoking] = useState<Device | null>(null);
  const [reason, setReason] = useState('');
  const revoke = useMutation({
    mutationFn: (d: Device) =>
      unwrap(
        api.POST('/sync/devices/{id}/revoke', {
          params: { path: { id: d.deviceId } },
          body: reason.trim() ? { reason: reason.trim() } : {},
        }),
      ),
    onSuccess: (d) => {
      void qc.invalidateQueries({ queryKey: ['sync'] });
      notify.success(`${d.name} can no longer sync`);
      setRevoking(null);
    },
    onError: (e) => notify.error(e),
  });
  const [reinstating, setReinstating] = useState<Device | null>(null);
  const reinstate = useMutation({
    mutationFn: (d: Device) =>
      unwrap(api.POST('/sync/devices/{id}/reinstate', { params: { path: { id: d.deviceId } } })),
    onSuccess: (d) => {
      void qc.invalidateQueries({ queryKey: ['sync'] });
      notify.success(`${d.name} can sync again once someone signs in on it`);
      setReinstating(null);
    },
    onError: (e) => notify.error(e),
  });
  if (q.error) return null;
  if (q.isLoading)
    return (
      <Stack spacing={1.5}>
        {[0, 1].map((i) => (
          <Skeleton key={i} variant="rounded" height={96} />
        ))}
      </Stack>
    );
  const devices = q.data?.devices ?? [];
  if (devices.length === 0)
    return (
      <Card>
        <EmptyState
          icon={<ComputerRounded />}
          title="No desktop devices yet"
          body="When you sign in on the desktop app it registers here, so you can see its sync health and revoke it if it is lost."
        />
      </Card>
    );
  return (
    <>
      <Box
        sx={{
          display: 'grid',
          gap: 2,
          gridTemplateColumns: { xs: 'minmax(0,1fr)', md: '1fr 1fr' },
        }}
      >
        {devices.map((d) => {
          const h = deviceHealth(d);
          return (
            <Card
              key={d.deviceId}
              sx={{ p: 2.25, '& .device-body': { opacity: d.revoked ? 0.65 : 1 } }}
            >
              <Stack direction="row" spacing={2} sx={{ alignItems: 'flex-start' }}>
                <IconBadge
                  icon={<ComputerRounded />}
                  tone={h.tone === 'neutral' ? 'neutral' : h.tone}
                  size={44}
                />
                <Box className="device-body" sx={{ flex: 1, minWidth: 0 }}>
                  <Stack
                    direction="row"
                    spacing={1}
                    sx={{ alignItems: 'center', flexWrap: 'wrap' }}
                  >
                    <Typography variant="subtitle1" sx={{ fontWeight: 650 }}>
                      {d.name}
                    </Typography>
                    <Chip
                      size="small"
                      label={h.label}
                      color={h.tone === 'neutral' ? 'default' : h.tone}
                      variant="outlined"
                      sx={{ height: 22, fontWeight: 600 }}
                    />
                  </Stack>
                  <Typography variant="body2" color="text.secondary">
                    {d.platform}
                    {d.appVersion ? ` · v${d.appVersion}` : ''} · series {d.docSeries}
                  </Typography>
                  <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5, mt: 1.5 }}>
                    <Field label="Last seen">
                      {d.lastSeenAt ? (
                        <Tooltip title={dateTime(d.lastSeenAt, format)}>
                          <span>{relativeTime(d.lastSeenAt)}</span>
                        </Tooltip>
                      ) : (
                        'Never'
                      )}
                    </Field>
                    <Field label="Behind server">{d.lag ?? '—'}</Field>
                    <Field label="Open conflicts">{d.openConflicts}</Field>
                    <Field label="Errors (24 h)">{d.errorsLast24h}</Field>
                  </Box>
                  {d.lastError && !d.revoked && (
                    <Alert severity="error" sx={{ mt: 1.5, py: 0 }}>
                      {commandLabel(d.lastError.commandType)}: {d.lastError.message}
                    </Alert>
                  )}
                  {d.revoked && (
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      component="div"
                      sx={{ mt: 1 }}
                    >
                      Revoked {d.revokedAt ? relativeTime(d.revokedAt) : ''}
                      {d.revokedReason ? ` — ${d.revokedReason}` : ''}
                    </Typography>
                  )}
                </Box>
                {d.revoked && (
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<RestoreRounded />}
                    onClick={() => setReinstating(d)}
                    sx={{ flexShrink: 0 }}
                  >
                    Restore access
                  </Button>
                )}
                {!d.revoked && (
                  <Tooltip title="Revoke this device">
                    <IconButton
                      aria-label={`Revoke ${d.name}`}
                      onClick={() => {
                        setReason('');
                        setRevoking(d);
                      }}
                    >
                      <BlockRounded fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
              </Stack>
            </Card>
          );
        })}
      </Box>
      <Dialog open={Boolean(revoking)} onClose={() => setRevoking(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Revoke {revoking?.name}?</DialogTitle>
        <DialogContent>
          <Typography color="text.secondary" sx={{ mb: 2 }}>
            The device is signed out and can no longer sync. Anything it has not pushed yet stays on
            that device. You can restore its access here later.
          </Typography>
          <TextField
            label="Reason (optional)"
            fullWidth
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="e.g. Laptop stolen"
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button variant="outlined" onClick={() => setRevoking(null)} disabled={revoke.isPending}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            loading={revoke.isPending}
            onClick={() => revoking && revoke.mutate(revoking)}
          >
            Revoke device
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={Boolean(reinstating)}
        onClose={() => setReinstating(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 700 }}>Restore {reinstating?.name}?</DialogTitle>
        <DialogContent>
          <Typography color="text.secondary">
            The computer can sync again with the same document series ({reinstating?.docSeries}),
            and sends anything it had not pushed yet. It stays signed out until someone signs in on
            it.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button
            variant="outlined"
            onClick={() => setReinstating(null)}
            disabled={reinstate.isPending}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            loading={reinstate.isPending}
            onClick={() => reinstating && reinstate.mutate(reinstating)}
          >
            Restore access
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

function useDeviceNames(): (id: string | null | undefined) => string {
  const { can } = useAuth();
  const q = useQuery({
    queryKey: ['sync', 'devices'],
    queryFn: () => unwrap(api.GET('/sync/devices')),
    enabled: can('devices.manage'),
    staleTime: 60_000,
  });
  return (id) =>
    id ? (q.data?.data.find((d) => d.deviceId === id)?.name ?? 'Desktop device') : '—';
}

const CONFLICT_STATUSES = [
  'OPEN',
  'RESOLVED_CLIENT',
  'RESOLVED_SERVER',
  'RESOLVED_MERGED',
] as const;

function ConflictsTab() {
  const p = useReportParams();
  const { format } = useShop();
  const deviceName = useDeviceNames();
  const statusParam = p.get('status');
  const status =
    statusParam === 'all'
      ? undefined
      : (CONFLICT_STATUSES.find((s) => s === statusParam) ?? 'OPEN');
  const query = { status, page: p.page, pageSize: p.pageSize };
  const q = useQuery({
    queryKey: ['sync', 'conflicts', query],
    queryFn: () => unwrap(api.GET('/sync/conflicts', { params: { query } })),
    placeholderData: keepPreviousData,
  });
  const [selected, setSelected] = useState<SyncConflict | null>(null);
  return (
    <>
      {q.error && <QueryError error={q.error} onRetry={() => void q.refetch()} />}
      <DataTable
        loading={q.isLoading}
        rows={q.data?.data}
        rowKey={(c) => c.id}
        page={q.data?.page}
        onPageChange={p.setPage}
        onPageSizeChange={p.setPageSize}
        onRowClick={setSelected}
        toolbar={
          <TextField
            select
            size="small"
            fullWidth={false}
            label="Status"
            value={status ?? 'all'}
            onChange={(e) =>
              p.update({ status: e.target.value === 'OPEN' ? null : e.target.value })
            }
            sx={{ minWidth: 200 }}
          >
            <MenuItem value="OPEN">Open</MenuItem>
            <MenuItem value="RESOLVED_CLIENT">Kept device version</MenuItem>
            <MenuItem value="RESOLVED_SERVER">Kept server version</MenuItem>
            <MenuItem value="RESOLVED_MERGED">Merged</MenuItem>
            <MenuItem value="all">All</MenuItem>
          </TextField>
        }
        empty={
          <EmptyState
            icon={<CheckCircleOutlineRounded />}
            title={status === 'OPEN' ? 'No conflicts to review' : 'No conflicts'}
            body="When two people change the same record while a device is offline, it shows up here for review."
          />
        }
        columns={[
          {
            key: 'what',
            header: 'Conflict',
            render: (c) => (
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {c.kind === 'NEGATIVE_STOCK_REVIEW'
                    ? 'Stock went negative'
                    : `${entityLabel(c.entityType)} edited in two places`}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {commandLabel(c.commandType)}
                </Typography>
              </Box>
            ),
          },
          {
            key: 'device',
            header: 'Device',
            hideBelow: 'sm',
            render: (c) => deviceName(c.deviceId),
          },
          {
            key: 'when',
            header: 'Raised',
            hideBelow: 'md',
            render: (c) => (
              <Tooltip title={dateTime(c.createdAt, format)}>
                <span>{relativeTime(c.createdAt)}</span>
              </Tooltip>
            ),
          },
          {
            key: 'status',
            header: 'Status',
            align: 'right',
            render: (c) => (
              <StatusChip
                status={c.status === 'OPEN' ? 'OPEN' : 'COMPLETED'}
                label={RESOLUTION_LABEL[c.status]}
              />
            ),
          },
        ]}
      />
      <ConflictDrawer
        conflict={selected}
        onClose={() => setSelected(null)}
        deviceName={deviceName}
      />
    </>
  );
}

function ConflictDrawer({
  conflict,
  onClose,
  deviceName,
}: {
  conflict: SyncConflict | null;
  onClose: () => void;
  deviceName: (id: string | null | undefined) => string;
}) {
  const { can } = useAuth();
  const { format } = useShop();
  const qc = useQueryClient();
  const notify = useNotify();
  const [merging, setMerging] = useState(false);
  const [mergeText, setMergeText] = useState('');
  const canResolve = can('sync.resolve_conflicts');
  const resolve = useMutation({
    mutationFn: (body: {
      resolution: 'KEEP_SERVER' | 'KEEP_CLIENT' | 'MERGED';
      mergedData?: Record<string, unknown>;
    }) =>
      unwrap(
        api.POST('/sync/conflicts/{id}/resolve', {
          params: { path: { id: conflict?.id ?? '' } },
          body,
        }),
      ),
    onSuccess: (c) => {
      void qc.invalidateQueries({ queryKey: ['sync'] });
      notify.success(`Conflict resolved — ${RESOLUTION_LABEL[c.status].toLowerCase()}`);
      setMerging(false);
      onClose();
    },
    onError: (e) => notify.error(e),
  });
  const parsed = parseMerged(mergeText);
  const rows = conflict ? conflictDiff(conflict) : [];
  const isStock = conflict?.kind === 'NEGATIVE_STOCK_REVIEW';
  return (
    <Drawer
      anchor="right"
      open={Boolean(conflict)}
      onClose={onClose}
      slotProps={{ paper: { sx: { width: { xs: '100%', sm: 560 }, p: 3 } } }}
    >
      {conflict && (
        <>
          <Stack
            direction="row"
            sx={{ alignItems: 'flex-start', justifyContent: 'space-between', mb: 2 }}
          >
            <Box>
              <Typography variant="h5">
                {isStock ? 'Stock went negative' : `${entityLabel(conflict.entityType)} conflict`}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {commandLabel(conflict.commandType)} · {deviceName(conflict.deviceId)} ·{' '}
                {dateTime(conflict.createdAt, format)}
              </Typography>
            </Box>
            <IconButton aria-label="Close" onClick={onClose}>
              <CloseRounded />
            </IconButton>
          </Stack>
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, mb: 2.5 }}>
            <Field label="Status">{RESOLUTION_LABEL[conflict.status]}</Field>
            <Field label="Versions">
              device based on v{conflict.baseVersion ?? '—'} · server v
              {conflict.serverVersion ?? '—'}
            </Field>
          </Box>
          {isStock ? (
            <>
              <Alert severity="warning" sx={{ mb: 2 }}>
                A sale on the device took stock below zero. The goods already left the shop, so the
                sale was kept. Check the count, adjust stock if needed, then acknowledge.
              </Alert>
              <JsonBlock value={conflict.details ?? conflict.clientPayload} />
            </>
          ) : (
            <>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                What differs
              </Typography>
              {rows.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  No field details were stored.
                </Typography>
              ) : (
                <Table size="small" sx={{ mb: 2 }}>
                  <TableHead>
                    <TableRow>
                      <TableCell>Field</TableCell>
                      <TableCell>On the server</TableCell>
                      <TableCell>From the device</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {rows.map((r) => (
                      <TableRow
                        key={r.field}
                        sx={{ bgcolor: r.changed ? 'action.hover' : undefined }}
                      >
                        <TableCell sx={{ color: 'text.secondary' }}>{r.field}</TableCell>
                        <TableCell
                          sx={{ wordBreak: 'break-word', fontWeight: r.changed ? 600 : undefined }}
                        >
                          {r.before}
                        </TableCell>
                        <TableCell
                          sx={{
                            wordBreak: 'break-word',
                            fontWeight: r.changed ? 600 : undefined,
                            color: r.changed ? 'primary.main' : undefined,
                          }}
                        >
                          {r.after}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </>
          )}
          {conflict.status === 'OPEN' && (
            <>
              <Divider sx={{ my: 2.5 }} />
              {!canResolve ? (
                <Alert severity="info">
                  Only staff allowed to resolve sync conflicts can settle this.
                </Alert>
              ) : isStock ? (
                <Button
                  variant="contained"
                  loading={resolve.isPending}
                  onClick={() => resolve.mutate({ resolution: 'KEEP_SERVER' })}
                >
                  Acknowledge
                </Button>
              ) : (
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25}>
                  <Button
                    variant="outlined"
                    loading={resolve.isPending && resolve.variables.resolution === 'KEEP_SERVER'}
                    disabled={resolve.isPending}
                    onClick={() => resolve.mutate({ resolution: 'KEEP_SERVER' })}
                  >
                    Keep server version
                  </Button>
                  <Button
                    variant="contained"
                    loading={resolve.isPending && resolve.variables.resolution === 'KEEP_CLIENT'}
                    disabled={resolve.isPending}
                    onClick={() => resolve.mutate({ resolution: 'KEEP_CLIENT' })}
                  >
                    Use device version
                  </Button>
                  <Button
                    startIcon={<MergeRounded />}
                    disabled={resolve.isPending}
                    onClick={() => {
                      setMergeText(mergeStart(conflict));
                      setMerging(true);
                    }}
                  >
                    Merge…
                  </Button>
                </Stack>
              )}
            </>
          )}
          {conflict.status !== 'OPEN' && conflict.resolvedAt && (
            <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block' }}>
              Resolved {relativeTime(conflict.resolvedAt)}
            </Typography>
          )}
          <Dialog open={merging} onClose={() => setMerging(false)} maxWidth="sm" fullWidth>
            <DialogTitle sx={{ fontWeight: 700 }}>Merge by hand</DialogTitle>
            <DialogContent>
              <Typography color="text.secondary" sx={{ mb: 2 }}>
                Starts from the server’s record with the device’s values on top. Edit the values to
                keep, then apply.
              </Typography>
              <TextField
                multiline
                minRows={10}
                fullWidth
                value={mergeText}
                onChange={(e) => setMergeText(e.target.value)}
                error={!parsed.ok}
                helperText={parsed.ok ? ' ' : parsed.error}
                slotProps={{
                  htmlInput: {
                    style: { fontFamily: 'ui-monospace, monospace', fontSize: 12.5 },
                    spellCheck: false,
                  },
                }}
              />
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2.5 }}>
              <Button variant="outlined" onClick={() => setMerging(false)}>
                Cancel
              </Button>
              <Button
                variant="contained"
                disabled={!parsed.ok}
                loading={resolve.isPending}
                onClick={() =>
                  parsed.ok && resolve.mutate({ resolution: 'MERGED', mergedData: parsed.value })
                }
              >
                Apply merge
              </Button>
            </DialogActions>
          </Dialog>
        </>
      )}
    </Drawer>
  );
}

function ErrorsTab() {
  const p = useReportParams();
  const { format } = useShop();
  const deviceName = useDeviceNames();
  const query = { page: p.page, pageSize: p.pageSize };
  const q = useQuery({
    queryKey: ['sync', 'errors', query],
    queryFn: () => unwrap(api.GET('/sync/errors', { params: { query } })),
    placeholderData: keepPreviousData,
  });
  return (
    <>
      {q.error && <QueryError error={q.error} onRetry={() => void q.refetch()} />}
      <DataTable
        loading={q.isLoading}
        rows={q.data?.data}
        rowKey={(e) => e.id}
        page={q.data?.page}
        onPageChange={p.setPage}
        onPageSizeChange={p.setPageSize}
        empty={
          <EmptyState
            icon={<CheckCircleOutlineRounded />}
            title="No sync errors"
            body="Commands a device could not apply would be listed here with the reason."
          />
        }
        columns={[
          {
            key: 'when',
            header: 'When',
            width: 140,
            render: (e) => (
              <Tooltip title={dateTime(e.occurredAt, format)}>
                <Stack direction="row" spacing={0.75} sx={{ alignItems: 'center' }}>
                  <ScheduleRounded sx={{ fontSize: 16, color: 'text.disabled' }} />
                  <span>{relativeTime(e.occurredAt)}</span>
                </Stack>
              </Tooltip>
            ),
          },
          {
            key: 'what',
            header: 'Error',
            render: (e) => (
              <Box>
                <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                  <ErrorOutlineRounded color="error" sx={{ fontSize: 18 }} />
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {commandLabel(e.commandType)}
                  </Typography>
                </Stack>
                <Typography variant="caption" color="text.secondary" component="div">
                  {e.message}
                </Typography>
              </Box>
            ),
          },
          {
            key: 'device',
            header: 'Device',
            hideBelow: 'md',
            render: (e) => deviceName(e.deviceId),
          },
          {
            key: 'code',
            header: 'Code',
            hideBelow: 'sm',
            render: (e) => (
              <Chip
                size="small"
                variant="outlined"
                label={e.errorCode}
                sx={{ fontFamily: 'ui-monospace, monospace', fontSize: 11 }}
              />
            ),
          },
          {
            key: 'retry',
            header: 'Retry',
            align: 'right',
            render: (e) =>
              e.retryable ? (
                <StatusChip status="OPEN" label="Will retry" />
              ) : (
                <StatusChip status="VOID" label="Needs action" />
              ),
          },
        ]}
      />
    </>
  );
}
